// src/pages/ProductsPage.jsx
import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getProducts, createProduct, updateProduct, deleteProduct } from '../lib/db';

const empty = () => ({ code:'', name:'', price:0, category:'Arrangement', description:'', active:true });
const CATEGORIES = ['Arrangement','Bouquet','Plant','Accessory','Chocolate','Balloon','Vase','Gift Basket','Other'];

export function ProductsPage() {
  const { shopId } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [showModal,setShowModal]= useState(false);
  const [form,     setForm]     = useState(empty());
  const [editId,   setEditId]   = useState(null);
  const [saving,   setSaving]   = useState(false);

  useEffect(() => { if (shopId) load(); }, [shopId]);
  async function load() {
    setLoading(true);
    try { setProducts(await getProducts(shopId)); }
    catch (e) { console.error('[ProductsPage] load failed', e); }
    finally { setLoading(false); }
  }

  const filtered = products.filter(p => {
    if (!search) return true;
    return `${p.name} ${p.code} ${p.category}`.toLowerCase().includes(search.toLowerCase());
  });

  function openNew()  { setForm(empty()); setEditId(null); setShowModal(true); }
  function openEdit(p){ setForm({...empty(),...p}); setEditId(p.id); setShowModal(true); }

  async function handleSave(e) {
    e.preventDefault(); setSaving(true);
    try {
      const data = { ...form, price: parseFloat(form.price)||0 };
      if (editId) { await updateProduct(shopId, editId, data); setProducts(ps => ps.map(p => p.id===editId?{...p,...data}:p)); }
      else        { const id = await createProduct(shopId, data); setProducts(ps => [...ps, {id,...data}]); }
      setShowModal(false);
    } finally { setSaving(false); }
  }

  async function toggle(p) {
    await updateProduct(shopId, p.id, { active: !p.active });
    setProducts(ps => ps.map(x => x.id===p.id ? {...x,active:!p.active} : x));
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type==='checkbox'?e.target.checked:e.target.value }));

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <input className="input" style={{maxWidth:280}} placeholder="Search products…" value={search} onChange={e=>setSearch(e.target.value)}/>
        <button className="btn btn-primary" onClick={openNew}>+ New Product</button>
      </div>

      {loading ? <div className="loading-center"><div className="spinner"/></div>
      : filtered.length === 0 ? (
        <div className="empty-state"><div className="empty-icon">🌹</div><h3>No products yet</h3><p>Add your flower arrangements, gifts, and more.</p></div>
      ) : (
        <div className="card" style={{padding:0}}>
          <table className="data-table">
            <thead><tr><th>Code</th><th>Name</th><th>Category</th><th>Price</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id}>
                  <td style={{fontFamily:'monospace',color:'var(--g600)',fontWeight:700}}>{p.code||'—'}</td>
                  <td style={{fontWeight:600}}>{p.name}</td>
                  <td><span className="pill pill-gray" style={{fontSize:11}}>{p.category}</span></td>
                  <td style={{fontWeight:600}}>${parseFloat(p.price||0).toFixed(2)}</td>
                  <td>
                    <button className={`pill ${p.active?'pill-green':'pill-red'}`} style={{cursor:'pointer',border:'none',background:p.active?'var(--g100)':'var(--red-pale)'}} onClick={()=>toggle(p)}>
                      {p.active?'Active':'Inactive'}
                    </button>
                  </td>
                  <td style={{display:'flex',gap:6}}>
                    <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(p)}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={async()=>{ if(!window.confirm('Delete?'))return; await deleteProduct(shopId,p.id); setProducts(ps=>ps.filter(x=>x.id!==p.id)); }}>Del</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)setShowModal(false)}}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editId?'Edit Product':'New Product'}</h2>
              <button className="modal-close" onClick={()=>setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="form-row">
                <div className="form-group w-sm"><label className="field-label">Code</label><input className="input" value={form.code} onChange={set('code')} placeholder="ROSE1"/></div>
                <div className="form-group flex1"><label className="field-label">Name *</label><input className="input" value={form.name} onChange={set('name')} required/></div>
              </div>
              <div className="form-row">
                <div className="form-group flex1"><label className="field-label">Category</label>
                  <select className="input" value={form.category} onChange={set('category')}>
                    {CATEGORIES.map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group w-sm"><label className="field-label">Price *</label>
                  <input className="input" type="number" step="0.01" min="0" value={form.price} onChange={set('price')} required/>
                </div>
              </div>
              <div className="form-group"><label className="field-label">Description</label><textarea className="input" rows={2} value={form.description} onChange={set('description')}/></div>
              <label className="checkbox-label"><input type="checkbox" checked={form.active} onChange={set('active')}/> Active (visible when creating orders)</label>
              <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16}}>
                <button type="button" className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving?'Saving…':'Save Product'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}


// ── EMPLOYEES PAGE ─────────────────────────────────────────────────────────────
import { getEmployees, createEmployee, updateEmployee, deleteEmployee } from '../lib/db';
import { formatInviteCode, inviteLink } from '../lib/invites';

export function EmployeesPage() {
  const { shopId, inviteEmployee, revokeInvite } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form,      setForm]      = useState({ displayName:'', email:'', role:'staff' });
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const [issued,    setIssued]    = useState(null); // freshly generated invite
  const [copied,    setCopied]    = useState('');

  useEffect(() => { if (shopId) load(); }, [shopId]);
  async function load() {
    setLoading(true);
    try { setEmployees(await getEmployees(shopId)); }
    catch (e) { console.error('[EmployeesPage] load failed', e); }
    finally { setLoading(false); }
  }

  async function handleInvite(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const { code } = await inviteEmployee(form);
      await load();
      // Keep the modal open — the code only exists here; if they close it
      // without copying, the invite is useless and must be reissued.
      setIssued({ code, ...form });
    } catch(err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function copy(text, what) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(''), 2000);
    } catch { setError('Could not copy — select the code and copy it manually.'); }
  }

  async function handleRevoke(emp) {
    if (!window.confirm(`Cancel the invite for ${emp.displayName || emp.email}?`)) return;
    await revokeInvite(emp.inviteCode, emp.id);
    await load();
  }

  function closeModal() {
    setShowModal(false); setIssued(null); setError('');
    setForm({ displayName:'', email:'', role:'staff' });
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const ROLE_LABELS = { owner:'Owner', manager:'Manager', staff:'Staff', designer:'Designer' };

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <h2 style={{color:'var(--g800)'}}>Team Members</h2>
        <button className="btn btn-primary" onClick={()=>setShowModal(true)}>+ Invite Employee</button>
      </div>

      {loading ? <div className="loading-center"><div className="spinner"/></div>
      : employees.length === 0 ? (
        <div className="empty-state"><div className="empty-icon">👤</div><h3>No team members yet</h3><p>Invite your staff to give them access.</p></div>
      ) : (
        <div className="card" style={{padding:0}}>
          <table className="data-table">
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {employees.map(emp => (
                <tr key={emp.id}>
                  <td style={{fontWeight:600}}>{emp.displayName||'—'}</td>
                  <td style={{color:'var(--text-mid)'}}>{emp.email}</td>
                  <td><span className="pill pill-blue" style={{fontSize:11}}>{ROLE_LABELS[emp.role]||emp.role}</span></td>
                  <td><span className={`pill ${emp.status==='active'?'pill-green':emp.status==='invited'?'pill-gold':'pill-gray'}`} style={{fontSize:11}}>{emp.status}</span></td>
                  <td style={{display:'flex',gap:6}}>
                    {emp.status === 'invited' && emp.inviteCode && (
                      <>
                        <button className="btn btn-ghost btn-sm" title="Copy invite link"
                          onClick={()=>copy(inviteLink(emp.inviteCode), emp.id)}>
                          {copied === emp.id ? '✓ Copied' : '🔗 Link'}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={()=>handleRevoke(emp)}>
                          Cancel invite
                        </button>
                      </>
                    )}
                    {emp.role !== 'owner' && emp.status !== 'invited' && (
                      <button className="btn btn-danger btn-sm" onClick={async()=>{ if(!window.confirm('Remove?'))return; await deleteEmployee(shopId,emp.id); setEmployees(es=>es.filter(e=>e.id!==emp.id)); }}>
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card" style={{background:'var(--blue-pale)',border:'1px solid #bfdbfe',marginTop:8}}>
        <p style={{fontSize:13,color:'#1d4ed8'}}>
          <strong>How invites work:</strong> Adding an employee generates a one-time
          invite code. Give them the code or the link — by text, email, or on paper —
          and they use it on the sign-in screen ("I have an invite code") to create
          their password and join <em>this</em> shop. Codes expire after 14 days and
          can only be used once.
        </p>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)closeModal()}}>
          <div className="modal">
            <div className="modal-header">
              <h2>{issued ? 'Invite created' : 'Invite Employee'}</h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            {error && <div className="auth-error">{error}</div>}

            {issued ? (
              <div>
                <p style={{fontSize:13,color:'var(--text-mid)',marginBottom:12}}>
                  Give this to <strong>{issued.displayName || issued.email}</strong>.
                  It works once and expires in 14 days.
                </p>
                <div style={{
                  background:'var(--parch)', border:'1px solid var(--border)',
                  borderRadius:'var(--radius)', padding:'16px', textAlign:'center', marginBottom:12
                }}>
                  <div style={{
                    fontFamily:'monospace', fontSize:26, fontWeight:800,
                    letterSpacing:'3px', color:'var(--g800)'
                  }}>
                    {formatInviteCode(issued.code)}
                  </div>
                </div>
                <div style={{display:'flex',gap:8,marginBottom:14}}>
                  <button className="btn btn-secondary" style={{flex:1}}
                    onClick={()=>copy(formatInviteCode(issued.code), 'code')}>
                    {copied === 'code' ? '✓ Copied' : 'Copy code'}
                  </button>
                  <button className="btn btn-primary" style={{flex:1}}
                    onClick={()=>copy(inviteLink(issued.code), 'link')}>
                    {copied === 'link' ? '✓ Copied' : 'Copy invite link'}
                  </button>
                </div>
                <div style={{
                  background:'var(--gold-pale)', border:'1px solid #f5d9a0',
                  borderRadius:'var(--radius-sm)', padding:'9px 12px',
                  fontSize:12.5, color:'var(--gold)', marginBottom:14
                }}>
                  ⚠️ Copy it now — this code isn't shown again. You can still copy the
                  link later from the Team list, or cancel and reissue.
                </div>
                <button className="btn btn-secondary" style={{width:'100%'}} onClick={closeModal}>Done</button>
              </div>
            ) : (
              <form onSubmit={handleInvite}>
                <div className="form-group"><label className="field-label">Full Name</label><input className="input" value={form.displayName} onChange={set('displayName')} required/></div>
                <div className="form-group"><label className="field-label">Email *</label><input className="input" type="email" value={form.email} onChange={set('email')} required/></div>
                <div className="form-group"><label className="field-label">Role</label>
                  <select className="input" value={form.role} onChange={set('role')}>
                    <option value="staff">Staff – Can create and manage orders</option>
                    <option value="designer">Designer – Can update design status</option>
                    <option value="manager">Manager – Full access except billing</option>
                  </select>
                </div>
                <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16}}>
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>{saving?'Creating…':'Create invite'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ── REPORTS PAGE ──────────────────────────────────────────────────────────────
import { getSalesSummary } from '../lib/db';

export function ReportsPage() {
  const { shopId } = useAuth();
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const fmt = n => '$' + (n||0).toFixed(2);

  useEffect(() => {
    if (!shopId) return;
    getSalesSummary(shopId).then(setSummary).finally(()=>setLoading(false));
  }, [shopId]);

  const totals = summary.reduce((acc,r) => ({
    today: { count: acc.today.count+r.today.count, total: acc.today.total+r.today.total },
    month: { count: acc.month.count+r.month.count, total: acc.month.total+r.month.total },
    year:  { count: acc.year.count +r.year.count,  total: acc.year.total +r.year.total  },
  }), { today:{count:0,total:0}, month:{count:0,total:0}, year:{count:0,total:0} });

  if (loading) return <div className="loading-center"><div className="spinner"/></div>;

  return (
    <div>
      <div className="stat-grid">
        {[
          { label:"Today's Sales",  value: fmt(totals.today.total), sub: `${totals.today.count} orders` },
          { label:"This Month",     value: fmt(totals.month.total), sub: `${totals.month.count} orders` },
          { label:"This Year",      value: fmt(totals.year.total),  sub: `${totals.year.count} orders`  },
          { label:"Avg Order",      value: fmt(totals.year.count ? totals.year.total/totals.year.count : 0), sub: 'year to date' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">Order Type Breakdown</span></div>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order Type</th>
                <th>Today — #</th><th>Today — $</th>
                <th>Month — #</th><th>Month — $</th>
                <th>Year — #</th><th>Year — $</th>
              </tr>
            </thead>
            <tbody>
              {summary.map(r => (
                <tr key={r.type}>
                  <td style={{fontWeight:600}}>{r.label}</td>
                  <td style={{textAlign:'center'}}>{r.today.count}</td>
                  <td>{fmt(r.today.total)}</td>
                  <td style={{textAlign:'center'}}>{r.month.count}</td>
                  <td>{fmt(r.month.total)}</td>
                  <td style={{textAlign:'center'}}>{r.year.count}</td>
                  <td style={{fontWeight:700,color:'var(--g700)'}}>{fmt(r.year.total)}</td>
                </tr>
              ))}
              <tr style={{background:'var(--g50)'}}>
                <td style={{fontWeight:800}}>Totals</td>
                <td style={{textAlign:'center',fontWeight:700}}>{totals.today.count}</td>
                <td style={{fontWeight:700}}>{fmt(totals.today.total)}</td>
                <td style={{textAlign:'center',fontWeight:700}}>{totals.month.count}</td>
                <td style={{fontWeight:700}}>{fmt(totals.month.total)}</td>
                <td style={{textAlign:'center',fontWeight:700}}>{totals.year.count}</td>
                <td style={{fontWeight:800,color:'var(--g700)',fontSize:15}}>{fmt(totals.year.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ── SETTINGS PAGE ─────────────────────────────────────────────────────────────
import { updateTenant } from '../lib/db';
import { workflowOf } from '../lib/priority';

const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export function SettingsPage() {
  const { shopId, shop } = useAuth();
  const [form,    setForm]    = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (shop) setForm({
      name:        shop.name        || '',
      phone:       shop.phone       || '',
      email:       shop.email       || '',
      address:     shop.address     || '',
      city:        shop.city        || 'Yellowknife',
      province:    shop.province    || 'NT',
      country:     shop.country     || 'Canada',
      taxRate:     (shop.taxRate    || 0.05) * 100,
      deliveryFee: shop.deliveryFee || 12,
      serviceFee:  shop.serviceFee  || 14.99,
      workflow:    workflowOf(shop),
    });
  }, [shop]);

  async function handleSave(e) {
    e.preventDefault(); setSaving(true); setSuccess('');
    try {
      const w = form.workflow;
      await updateTenant(shopId, {
        ...form,
        taxRate: parseFloat(form.taxRate)/100,
        deliveryFee: parseFloat(form.deliveryFee),
        serviceFee:  parseFloat(form.serviceFee),
        workflow: {
          closedDays:        w.closedDays,
          baseLeadDays:      parseInt(w.baseLeadDays, 10)      || 1,
          bigOrderThreshold: parseFloat(w.bigOrderThreshold)   || 200,
          bigOrderLeadDays:  parseInt(w.bigOrderLeadDays, 10)  || 2,
          hugeOrderThreshold:parseFloat(w.hugeOrderThreshold)  || 500,
          hugeOrderLeadDays: parseInt(w.hugeOrderLeadDays, 10) || 3,
          outOfTownLeadDays: parseInt(w.outOfTownLeadDays, 10) || 2,
          airportCutoffMins: parseInt(w.airportCutoffMins, 10) || 90,
        },
      });
      setSuccess('Settings saved!');
    } finally { setSaving(false); }
  }

  if (!form) return <div className="loading-center"><div className="spinner"/></div>;
  const set  = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setW = k => e => setForm(f => ({ ...f, workflow: { ...f.workflow, [k]: e.target.value } }));
  const toggleClosed = (d) => setForm(f => {
    const cur = f.workflow.closedDays || [];
    return { ...f, workflow: { ...f.workflow,
      closedDays: cur.includes(d) ? cur.filter(x => x !== d) : [...cur, d].sort() } };
  });

  return (
    <div style={{maxWidth:600}}>
      {success && <div style={{background:'var(--g100)',border:'1px solid var(--g300)',color:'var(--g700)',padding:'10px 14px',borderRadius:'var(--radius-sm)',marginBottom:14}}>{success}</div>}
      <form onSubmit={handleSave}>
        <div className="card">
          <div className="section-label">Shop Info</div>
          <div className="form-group"><label className="field-label">Shop Name</label><input className="input" value={form.name} onChange={set('name')}/></div>
          <div className="form-row">
            <div className="form-group flex1"><label className="field-label">Phone</label><input className="input" value={form.phone} onChange={set('phone')}/></div>
            <div className="form-group flex1"><label className="field-label">Email</label><input className="input" type="email" value={form.email} onChange={set('email')} placeholder="hello@myflowers.com"/></div>
          </div>
          <div className="form-group"><label className="field-label">Address</label><input className="input" value={form.address} onChange={set('address')}/></div>
          <div className="form-row">
            <div className="form-group flex1"><label className="field-label">City</label><input className="input" value={form.city} onChange={set('city')}/></div>
            <div className="form-group w-sm"><label className="field-label">Province</label><input className="input" value={form.province} onChange={set('province')}/></div>
            <div className="form-group flex1"><label className="field-label">Country</label><select className="input" value={form.country} onChange={set('country')}><option>Canada</option><option>USA</option></select></div>
          </div>
        </div>

        <div className="card">
          <div className="section-label">Pricing Defaults</div>
          <div className="form-row">
            <div className="form-group flex1"><label className="field-label">Tax Rate (%)</label><input className="input" type="number" step="0.01" min="0" max="30" value={form.taxRate} onChange={set('taxRate')}/></div>
            <div className="form-group flex1"><label className="field-label">Delivery Fee ($)</label><input className="input" type="number" step="0.01" min="0" value={form.deliveryFee} onChange={set('deliveryFee')}/></div>
            <div className="form-group flex1"><label className="field-label">Wire Out Service Fee ($)</label><input className="input" type="number" step="0.01" min="0" value={form.serviceFee} onChange={set('serviceFee')}/></div>
          </div>
          <p style={{fontSize:12,color:'var(--text-muted)'}}>These values are auto-filled when creating new orders but can be changed per-order.</p>
        </div>

        {/* ── Workboard rules ── */}
        <div className="card">
          <div className="section-label">Workboard &amp; Lead Times</div>
          <p style={{fontSize:12.5,color:'var(--text-muted)',marginBottom:12}}>
            These rules decide what the Workboard shouts about. Lead times are counted in
            <strong> open days</strong>, so closed days are skipped automatically — an order
            needed Monday with a 1-day lead shows up on Saturday.
          </p>

          <div className="form-group">
            <label className="field-label">Days the shop is closed</label>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {DAY_NAMES.map((d, i) => {
                const off = (form.workflow.closedDays || []).includes(i);
                return (
                  <button key={d} type="button"
                    className={`btn btn-sm ${off ? 'btn-danger' : 'btn-secondary'}`}
                    onClick={() => toggleClosed(i)}>
                    {d}{off ? ' ✕' : ''}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group flex1"><label className="field-label">Make everything this many open days early</label>
              <input className="input" type="number" min="0" max="7" value={form.workflow.baseLeadDays} onChange={setW('baseLeadDays')}/>
            </div>
            <div className="form-group flex1"><label className="field-label">Out-of-town lead (open days)</label>
              <input className="input" type="number" min="0" max="14" value={form.workflow.outOfTownLeadDays} onChange={setW('outOfTownLeadDays')}/>
            </div>
            <div className="form-group flex1"><label className="field-label">Airport cutoff (minutes before departure)</label>
              <input className="input" type="number" min="0" max="600" step="15" value={form.workflow.airportCutoffMins} onChange={setW('airportCutoffMins')}/>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group flex1"><label className="field-label">Big order over ($)</label>
              <input className="input" type="number" min="0" step="10" value={form.workflow.bigOrderThreshold} onChange={setW('bigOrderThreshold')}/>
            </div>
            <div className="form-group w-sm"><label className="field-label">…start (days)</label>
              <input className="input" type="number" min="0" max="14" value={form.workflow.bigOrderLeadDays} onChange={setW('bigOrderLeadDays')}/>
            </div>
            <div className="form-group flex1"><label className="field-label">Very big order over ($)</label>
              <input className="input" type="number" min="0" step="10" value={form.workflow.hugeOrderThreshold} onChange={setW('hugeOrderThreshold')}/>
            </div>
            <div className="form-group w-sm"><label className="field-label">…start (days)</label>
              <input className="input" type="number" min="0" max="14" value={form.workflow.hugeOrderLeadDays} onChange={setW('hugeOrderLeadDays')}/>
            </div>
          </div>
        </div>

        <div className="card" style={{background:'var(--gold-pale)',border:'1px solid #f5d9a0'}}>
          <div className="section-label" style={{color:'var(--gold)'}}>Subscription</div>
          <p style={{fontSize:13,color:'var(--text-mid)',marginBottom:10}}>
            You're on the <strong>Standard Plan</strong> at $50/month. 
            To manage your subscription, contact <a href="mailto:support@ankushguptatech.com" style={{color:'var(--gold)'}}>support@ankushguptatech.com</a>
          </p>
        </div>

        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
      </form>
    </div>
  );
}
